import { Col, Row, TablePaginationConfig } from 'antd';
import { useLocationsDataContext } from 'context/LocationsDataContext';
import {
  CompanySetUpTabHash,
  LocationTableFields,
  pageSize,
} from 'enums/CompanySetupEnums';
import React, { FC, useCallback, useEffect, useState } from 'react';
import ParentCompanySetupService from 'services/ParentCompanySetup/parentCompanySetup.service';
import COPagination from 'shared/components/COPagination';
import COTable from 'shared/components/COTable';
import useFilters from 'shared/hooks/useFilters';
import styles from './locationDetails.module.scss';
import { debounce } from 'lodash';
import LocationTableColumns from './locationTable.columns';
import {
  FilterValue,
  SortOrder,
  SorterResult,
  TableCurrentDataSource,
} from 'antd/es/table/interface';
import { LocationModel } from 'models/CompanySetup/location.model';
import { SortingTypesEnums } from 'enums/sortingTypes.enum';
import { useParams } from 'react-router-dom';
import { filterLocationParams } from '../organisationDetails.util';
import { defaultYearTab } from 'constants/constants';
import useRedirect from 'shared/utils/useRedirect';

interface OrganizationDetailsFetchProps {
  filterParams: FetchLocationProps;
  updateFilters: (params?: FetchLocationProps) => void;
  activeTab: string;
}
export interface FetchLocationProps {
  year?: number;
  page?: number;
  search?: string;
  sortBy?: string;
  sortDir?: string;
}
export interface SortingProps {
  columnKey?: string | number | null;
  order?: string | null;
}

const OrganizationLocationDetails: FC<OrganizationDetailsFetchProps> = ({
  filterParams,
  updateFilters,
  activeTab,
}) => {
  const { id: organisationId } = useParams();
  const [sortedInfo, setSortedInfo] = useState<SortingProps>({
    columnKey: filterParams.sortBy ?? '',
    order: filterParams.sortDir ?? '',
  });

  const { setParams } = useFilters();
  const { redirectToActivityDataEntry } = useRedirect();
  const { fetchAllLocations, loading } = ParentCompanySetupService();
  const { locationTableData } = useLocationsDataContext();

  const debouncedSearch = useCallback(
    debounce((params: FetchLocationProps) => {
      //Filtering params to remove empty ones
      const filteredParams = filterLocationParams(params);
      //Cleaning up params to send as expected convention
      const modifiedParams = {
        ...filteredParams,
        sort_dir:
          filteredParams.sortDir === SortingTypesEnums.ASCEND
            ? LocationTableFields.ASC
            : filteredParams.sortDir === SortingTypesEnums.DESCEND
            ? LocationTableFields.DESC
            : '',
        sort_by: filteredParams.sortBy,
      };
      delete modifiedParams.sortDir;
      delete modifiedParams.sortBy;
      //Set the updated params as url and fetch data
      setParams(filteredParams ?? {}, activeTab);
      fetchAllLocations({ ...modifiedParams, organization_id: organisationId });
    }, 300),
    [organisationId]
  );

  useEffect(() => {
    debouncedSearch(filterParams);
  }, [filterParams, organisationId]);

  const handlePageChange = (page: number) => updateFilters({ page });

  const handleTableChange = (
    _: TablePaginationConfig,
    __: Record<string, FilterValue | null>,
    sorter: SorterResult<LocationModel> | SorterResult<LocationModel>[],
    ___: TableCurrentDataSource<LocationModel>
  ) => {
    if (sorter) {
      const isSingleSorter = !Array.isArray(sorter);
      const currentSorter = isSingleSorter ? sorter : sorter[0];
      const { columnKey, order } = currentSorter;
      setSortedInfo({ columnKey: columnKey, order: order as SortOrder });
      updateFilters({
        sortBy: order ? (columnKey as string) : '',
        sortDir: order ?? '',
      });
    }
  };

  const handleOnRowEvents = (record: LocationModel) =>
    redirectToActivityDataEntry(String(record?.id), {
      activeTab: defaultYearTab,
    });

  return (
    <Row gutter={[0, 20]} className={styles.detailsPage}>
      <Col span={24}>
        <COTable
          loading={loading}
          columns={LocationTableColumns({
            sortedInfo,
          })}
          data={locationTableData?.data}
          onChange={handleTableChange}
          handleRowClick={handleOnRowEvents}
        />
      </Col>
      <Col span={24}>
        <Row justify="end" className={styles.pagination}>
          <Col>
            <COPagination
              onChange={handlePageChange}
              total={locationTableData?.totalItems ?? 0}
              pageSize={pageSize}
              current={locationTableData?.currentPage}
            />
          </Col>
        </Row>
      </Col>
    </Row>
  );
};

export default OrganizationLocationDetails;
